
import { Component, Vue, Watch } from 'vue-property-decorator'
import request from '@/utils/request'
import { AdminApi } from '@/wwapi/apis/admin-api'
import { UserModel } from '@/wwapi/models'
import { GetUsersResponse } from '@/wwapi/models/get-users-response'
import { getToken } from '@/utils/cookies'
import { defaultUserData } from '../../defaultModels/IUserData'
import { Form } from 'element-ui'
import {
  getUserEntitlements,
  grantEntitlement,
  revokeEntitlementById,
  listFeatures,
  Entitlement,
  Feature,
  GrantEntitlementRequest
} from '@/api/entitlements'
import { getUserOtaInfo, clearUserOtaOverride } from '@/api/userOtaOverride'
import { getUserLocations, saveUserLocation, updateUserLocation, deleteUserLocation } from '@/api/userLocations'
import { getUserRoutes, updateUserRoute, deleteUserRoute } from '@/api/userRoutes'
import PinList, { PinListItem } from '@/components/ota/PinList.vue'
import RouteFormDialog from '@/components/RouteFormDialog/index.vue'
import PinForm from '@/components/ota/PinForm.vue'

const boolTypeOptions = [
  { key: 'true', displayName: 'true' },
  { key: 'false', displayName: 'false' },
  { key: '', displayName: 'leave empty' }
]

const loginTypeOptions = [
  { key: 'apple', displayName: 'Apple' },
  { key: 'google', displayName: 'Google' },
  { key: 'facebook', displayName: 'Facebook' },
  { key: 'email', displayName: 'Email' },
  { key: 'anonymous', displayName: 'Anonymous' },
  { key: '', displayName: 'Leave Empty' }
]

const userTypeOptions = [
  { key: 'admin', displayName: 'Admin' },
  { key: 'user', displayName: 'User' },
  { key: 'trainer', displayName: 'Trainer' },
  { key: '', displayName: 'Leave Empty' }
]
@Component({
  name: 'Users',
  components: { PinList, PinForm, RouteFormDialog }
})
export default class extends Vue {
  private list: UserModel[] = [];
  private applicationRegions: string[] = [];
  private listLoading = true;
  private boolTypeOptions = boolTypeOptions;
  private loginTypeOptions = loginTypeOptions;
  private userTypeOptions = userTypeOptions;
  private tempUserData: any = Object.assign({}, defaultUserData);

  // ===== Cihazlar ve OTA tab state =====
  // Lazy-loaded on first tab open + after pin mutations. PinForm runs in
  // device-scope mode (one card per cane), so the OTA dropdown auto-filters
  // to the cane's major and the user-scope toggle stays hidden.
  private devicesTabLoading = false
  private devicesTabUserDevices: any[] = []
  private devicesTabAllPins: PinListItem[] = []
  private devicesTabAvailableOtaFiles: any[] = []
  private devicesTabPinRemovingKey: string | null = null
  private devicesTabLoadedFor: string | null = null
  private userDeviceData: any = null;
  private deviceDetailVisible = false
  private deviceDetailLoading = false
  private deviceDetailData: any = null
  private deviceFeatures: any[] = []
  private deviceFeaturesLoading = false
  private dialogFormVisible = false;
  private purgeLoading = false;
  private dialogStatus = '';
  private textMap = {
    update: 'Edit',
    create: 'Create'
  };

  private rules = {
    firstName: [{ required: true, message: 'First name is required', trigger: 'blur' }],
    lastName: [{ required: true, message: 'Last name is required', trigger: 'blur' }],
    email: [
      { required: true, message: 'Email is required', trigger: 'blur' },
      { type: 'email', message: 'Please enter a valid email', trigger: 'blur' }
    ]
  };

  private listQuery = {
    isPremium: null,
    isAssistant: null,
    loginType: null,
    userType: null,
    platform: null,
    text: null,
    sort: '+id',
    page: 1,
    limit: 20
  };

  private total = 0;

  private pickerOptions = {
    disabledDate(time: Date) {
      // Disable dates before today
      return time.getTime() < Date.now() - 8.64e7
    }
  };

  // Saved places (kayıtlı yerler) for the selected user
  private userLocations: any[] = []
  private loadingLocations = false
  private locationDialogVisible = false
  private locationDialogStatus: 'create' | 'update' = 'create'
  private savingLocation = false
  private locationForm: any = { title: '', address: '', latitude: null, longitude: null, locationType: 'mylocation' }

  // Saved routes (kayıtlı rotalar) for the selected user
  private userRoutes: any[] = []
  private loadingRoutes = false
  private routeCreateVisible = false
  private routeDialogVisible = false
  private savingRoute = false
  private routeForm: any = { routeId: '', title: '', description: '', visibility: 'private' }

  // Tab and Entitlements data
  private activeTab = 'basic'

  // Activity Logs
  private userLogs: any[] = []
  private logsLoading = false
  private logHours = 24
  private logSeverity = 'WARNING'
  private userEntitlements: Entitlement[] = []
  private availableFeatures: Feature[] = []
  private entitlementsLoading = false
  private consultSubscriptions: any[] = []
  private consultSubsLoading = false
  private consultSubsTotal = 0
  private consultSubsPage = 1
  private consultSubsLimit = 10
  private consultSubsSearch = ''
  private consultActiveCredits = { totalSeconds: 0, totalMinutes: 0, activeCount: 0 }
  private deletingConsultSubId: number | null = null
  private grantDialogVisible = false
  private extendDialogVisible = false
  private selectedEntitlement: Entitlement | null = null
  private grantFormData = {
    featureKey: '',
    durationType: 'days' as 'days' | 'months' | 'years' | 'unlimited',
    durationValue: 365,
    sourceType: 'admin_grant'
  }

  private extendFormData = {
    durationValue: 30,
    durationType: 'days' as 'days' | 'months' | 'years'
  }

  // Subscription data
  private subscriptionsLoading = false
  private userTransactions: any[] = []
  private userSubscriptionEvents: any[] = []
  private subscriptionSummary: any = null

  created() {
    this.fetchData()
  }

  private async fetchData() {
    try {
      this.listLoading = true
      const adminApi = new AdminApi()
      if (this.listQuery.isPremium === '') {
        this.listQuery.isPremium = null
      }

      if (this.listQuery.isAssistant === '') {
        this.listQuery.isAssistant = null
      }

      if (this.listQuery.loginType === '') {
        this.listQuery.loginType = null
      }
      if (this.listQuery.userType === '') {
        this.listQuery.userType = null
      }
      if (this.listQuery.platform === '') {
        this.listQuery.platform = null
      }
      const response = await adminApi.userListPost(this.listQuery)
      const applicationRegionResponse = await adminApi.getClientSettings()
      const regions = applicationRegionResponse.data.data.find(
        (x) => x.key === 'CONSULT_REGIONS'
      )
      this.applicationRegions = regions!.value.split(';')
      const responseModel: GetUsersResponse = response.data
      const users: UserModel[] = responseModel.data
      // Get total count from response
      this.total = (response.data as any).total || 0
      if (users.length && users.length > 0) {
        users.map((user) => {
          if (user.consultSeconds) {
            user.consultSeconds = user.consultSeconds / 60
          }
        })
        this.list = users
      } else this.list = []
      console.log('data returned from getusers' + JSON.stringify(users))

      // this.list = response.data.items;
      // Just to simulate the time of the request
      setTimeout(() => {
        this.listLoading = false
      }, 0.5 * 1000)
    } catch (error) {
      alert(error)
    }
  }

  private handleFilter() {
    this.listQuery.page = 1
    this.fetchData()
  }

  private handleSizeChange(val: number) {
    this.listQuery.limit = val
    this.listQuery.page = 1
    this.fetchData()
  }

  private handleCurrentChange(val: number) {
    this.listQuery.page = val
    this.fetchData()
  }

  private async handleToggleCanUseConsult(row: any, index: number) {
    try {
      const adminApi = new AdminApi()
      const options = {
        headers: {
          Authorization: `Bearer ${getToken()}`
        }
      }
      this.listLoading = true
      const response = await adminApi.userToggleCanUseConsult(
        {
          userId: index
        },
        options
      )
      console.log('res.status: ' + response.status)
      if (response.status !== 200) {
        this.$notify({
          title: 'Error',
          dangerouslyUseHTMLString: true,
          message: 'An error occured. Message: ' + response.data.status.message,
          type: 'error',
          duration: 2000
        })
        this.listLoading = false
      } else {
        this.list = this.list.map((l) => {
          if (l.userId === index) {
            l.canUseConsult = response.data.data.canUseConsult
          }
          return l
        })
        this.listLoading = false

        this.$notify({
          title: 'Success',
          dangerouslyUseHTMLString: true,
          message:
            'User use consult value changed to <b>' +
            response.data.data.canUseConsult +
            '</b> for user <b>' +
            row.email +
            '</b>',
          type: 'success',
          duration: 2000
        })
      }
    } catch (err) {
      this.$notify({
        title: 'Error',
        dangerouslyUseHTMLString: true,
        message: 'An error occured. Message: ' + err.message,
        type: 'error',
        duration: 2000
      })
      this.listLoading = false
    }
  }

  private handleTabClick(tab: any) {
    if (tab && tab.name === 'locations') {
      this.fetchUserLocations()
    } else if (tab && tab.name === 'routes') {
      this.fetchUserRoutes()
    }
  }

  private async fetchUserRoutes() {
    const userId = this.tempUserData && this.tempUserData.userId
    if (!userId) return
    this.loadingRoutes = true
    try {
      const res: any = await getUserRoutes(userId)
      const body = res && res.data
      this.userRoutes = (body && Array.isArray(body.data)) ? body.data : []
    } catch (e) {
      this.$message.error(this.$t('savedRoutes.fetchFailed') as string)
    } finally {
      this.loadingRoutes = false
    }
  }

  private openEditRoute(row: any) {
    this.routeForm = {
      routeId: row.routeId,
      title: row.title,
      description: row.description,
      visibility: row.visibility || 'private'
    }
    this.routeDialogVisible = true
  }

  private async submitRoute() {
    const userId = this.tempUserData && this.tempUserData.userId
    if (!userId || !this.routeForm.routeId) return
    this.savingRoute = true
    try {
      await updateUserRoute(userId, this.routeForm.routeId, {
        title: this.routeForm.title,
        description: this.routeForm.description,
        visibility: this.routeForm.visibility
      })
      this.$message.success(this.$t('savedRoutes.saved') as string)
      this.routeDialogVisible = false
      await this.fetchUserRoutes()
    } catch (e: any) {
      const msg = e && e.response && e.response.data && e.response.data.status && e.response.data.status.message
      this.$message.error(msg || (this.$t('savedRoutes.saveFailed') as string))
    } finally {
      this.savingRoute = false
    }
  }

  private handleDeleteRoute(row: any) {
    const userId = this.tempUserData && this.tempUserData.userId
    if (!userId) return
    this.$confirm(this.$t('savedRoutes.deleteConfirm') as string, this.$t('savedRoutes.confirmTitle') as string, { type: 'warning' })
      .then(async() => {
        try {
          await deleteUserRoute(userId, row.routeId)
          this.$message.success(this.$t('savedRoutes.deleted') as string)
          await this.fetchUserRoutes()
        } catch (e) {
          this.$message.error(this.$t('savedRoutes.deleteFailed') as string)
        }
      })
      .catch(() => { /* cancelled */ })
  }

  private formatRouteDistance(m?: number) {
    if (m == null) return '-'
    return m >= 1000 ? (m / 1000).toFixed(1) + ' km' : m + ' m'
  }

  private async fetchUserLocations() {
    const userId = this.tempUserData && this.tempUserData.userId
    if (!userId) return
    this.loadingLocations = true
    try {
      const res: any = await getUserLocations(userId)
      const body = res && res.data
      this.userLocations = (body && Array.isArray(body.data)) ? body.data : []
    } catch (e) {
      this.$message.error(this.$t('savedPlaces.fetchFailed') as string)
    } finally {
      this.loadingLocations = false
    }
  }

  private openAddLocation() {
    this.locationDialogStatus = 'create'
    this.locationForm = { title: '', address: '', latitude: null, longitude: null, locationType: 'mylocation' }
    this.locationDialogVisible = true
  }

  private openEditLocation(row: any) {
    this.locationDialogStatus = 'update'
    this.locationForm = Object.assign({}, row)
    this.locationDialogVisible = true
  }

  private async submitLocation() {
    const userId = this.tempUserData && this.tempUserData.userId
    if (!userId) return
    if (this.locationDialogStatus === 'create') {
      if (this.locationForm.latitude == null || this.locationForm.longitude == null) {
        this.$message.warning(this.$t('savedPlaces.coordsRequired') as string)
        return
      }
    }
    this.savingLocation = true
    try {
      if (this.locationDialogStatus === 'create') {
        await saveUserLocation(userId, {
          latitude: Number(this.locationForm.latitude),
          longitude: Number(this.locationForm.longitude),
          title: this.locationForm.title,
          address: this.locationForm.address,
          locationType: this.locationForm.locationType
        })
      } else {
        await updateUserLocation(userId, this.locationForm.locationId, {
          title: this.locationForm.title,
          address: this.locationForm.address
        })
      }
      this.$message.success(this.$t('savedPlaces.saved') as string)
      this.locationDialogVisible = false
      await this.fetchUserLocations()
    } catch (e: any) {
      const msg = e && e.response && e.response.data && e.response.data.status && e.response.data.status.message
      this.$message.error(msg || (this.$t('savedPlaces.saveFailed') as string))
    } finally {
      this.savingLocation = false
    }
  }

  private handleDeleteLocation(row: any) {
    const userId = this.tempUserData && this.tempUserData.userId
    if (!userId) return
    this.$confirm(this.$t('savedPlaces.deleteConfirm') as string, this.$t('savedPlaces.confirmTitle') as string, { type: 'warning' })
      .then(async() => {
        try {
          await deleteUserLocation(userId, row.locationId)
          this.$message.success(this.$t('savedPlaces.deleted') as string)
          await this.fetchUserLocations()
        } catch (e) {
          this.$message.error(this.$t('savedPlaces.deleteFailed') as string)
        }
      })
      .catch(() => { /* cancelled */ })
  }

  private handleUpdate(row: any) {
    if (row.client === '1') {
      console.log('ios')
    } else {
      console.log('android')
    }

    // Create fresh copy with default values first, then override with row data
    this.tempUserData = Object.assign({}, defaultUserData, row)

    // Reset new fields for adding consult time
    this.$set(this.tempUserData, 'addConsultMinutes', 0)

    // Set default expiry date to 30 days from now
    const expiryDate = new Date()
    expiryDate.setDate(expiryDate.getDate() + 30)
    this.$set(this.tempUserData, 'consultExpiresDate', expiryDate)

    this.dialogStatus = 'update'
    this.dialogFormVisible = true
    this.loadUserDevice(row.userId)
    this.fetchConsultSubscriptions()
    this.$nextTick(() => {
      (this.$refs.dataForm as Form).clearValidate()
    })
  }

  get activeConsultSeconds(): number {
    return this.consultActiveCredits.totalSeconds
  }

  get activeConsultMinutes(): number {
    return this.consultActiveCredits.totalMinutes
  }

  get activeConsultCount(): number {
    return this.consultActiveCredits.activeCount
  }

  get userDeviceDisplay(): string {
    if (!this.userDeviceData) return ''
    return `${this.userDeviceData.serial_number || this.userDeviceData.serialNumber} (${this.userDeviceData.product_type || this.userDeviceData.productType})`
  }

  get userDeviceLifecycle(): string {
    if (!this.userDeviceData) return ''
    return this.userDeviceData.lifecycle_status || this.userDeviceData.lifecycleStatus || 'UNKNOWN'
  }

  get userDeviceLifecycleTag(): string {
    switch (this.userDeviceLifecycle) {
      case 'ACTIVE': return 'success'
      case 'INACTIVE': return 'info'
      case 'REPAIR': return 'warning'
      case 'REFUNDED':
      case 'REPLACED':
      case 'ARCHIVED':
        return 'danger'
      default: return ''
    }
  }

  get userDeviceActivationPending(): boolean {
    if (!this.userDeviceData) return false
    if (this.userDeviceLifecycle !== 'ACTIVE') return false
    const completedAt = this.userDeviceData.activation_completed_at || this.userDeviceData.activationCompletedAt
    return !completedAt
  }

  private async openDeviceDetailFromUser() {
    if (!this.userDeviceData) return
    const serial = this.userDeviceData.serial_number || this.userDeviceData.serialNumber
    if (!serial) return
    this.deviceDetailData = null
    this.deviceFeatures = []
    this.deviceDetailLoading = true
    this.deviceFeaturesLoading = true
    this.deviceDetailVisible = true
    try {
      const [deviceRes, featuresRes] = await Promise.all([
        request({ url: `/devices/${serial}`, method: 'get' }),
        request({ url: `/devices/${serial}/features`, method: 'get', params: { includeExpired: true } })
      ])
      this.deviceDetailData = deviceRes.data?.data || deviceRes.data
      this.deviceFeatures = featuresRes.data?.data || featuresRes.data || []
    } catch {
      this.deviceDetailData = null
      this.deviceFeatures = []
    } finally {
      this.deviceDetailLoading = false
      this.deviceFeaturesLoading = false
    }
  }

  private async fetchConsultSubscriptions() {
    if (!this.tempUserData?.userId) return
    this.consultSubsLoading = true
    try {
      const params: any = {
        page: this.consultSubsPage,
        limit: this.consultSubsLimit
      }
      if (this.consultSubsSearch) {
        params.search = this.consultSubsSearch
      }
      const { data } = await request({
        url: `/admin/users/${this.tempUserData.userId || this.tempUserData.user_id}/consult-subscriptions`,
        method: 'get',
        params
      })
      this.consultSubscriptions = data.data || []
      this.consultSubsTotal = data.total || 0
      this.consultActiveCredits = data.activeCredits || { totalSeconds: 0, totalMinutes: 0, activeCount: 0 }
    } catch (error) {
      console.error('Failed to load consult subscriptions', error)
      this.consultSubscriptions = []
      this.consultSubsTotal = 0
      this.consultActiveCredits = { totalSeconds: 0, totalMinutes: 0, activeCount: 0 }
    } finally {
      this.consultSubsLoading = false
    }
  }

  private async handleDeleteConsultSubscription(row: any) {
    if (!this.tempUserData?.userId && !this.tempUserData?.user_id) return
    if (new Date(row.expiresDate) <= new Date()) {
      this.$message.warning('Cannot delete expired subscription')
      return
    }

    try {
      await this.$confirm(
        `Delete this active credit (${Math.round(row.duration / 60)} min, expires ${this.formatDate(row.expiresDate)})? This cannot be undone.`,
        'Delete Active Credit',
        { confirmButtonText: 'Delete', cancelButtonText: 'Cancel', type: 'warning' }
      )
    } catch {
      return // user cancelled
    }

    this.deletingConsultSubId = row.id
    try {
      const userId = this.tempUserData.userId || this.tempUserData.user_id
      await request({
        url: `/admin/users/${userId}/consult-subscriptions/${row.id}`,
        method: 'delete'
      })
      this.$message.success('Credit deleted')
      await this.fetchConsultSubscriptions()
    } catch (error: any) {
      const msg = error?.response?.data?.status?.message || 'Failed to delete credit'
      this.$message.error(msg)
    } finally {
      this.deletingConsultSubId = null
    }
  }

  private async loadUserDevice(userId: string) {
    this.userDeviceData = null
    try {
      const { data } = await request({ url: `/users/${userId}/devices`, method: 'get' })
      const devices = data?.data || data || []
      // Prefer ACTIVE, then fall through to anything still bound to this
      // user (e.g. a REFUNDED / REPAIR record). Previously we only showed
      // ACTIVE and a half-bound device disappeared entirely from this card.
      const active = devices.find((d: any) => (d.lifecycle_status || d.lifecycleStatus) === 'ACTIVE')
      this.userDeviceData = active || devices[0] || null
    } catch {
      // silent
    }
  }

  private updateData() {
    const adminApi = new AdminApi();
    (this.$refs.dataForm as Form).validate(async(valid) => {
      if (valid) {
        const tempData = Object.assign({}, this.tempUserData)

        // Only send consultSeconds if user is adding time (addConsultMinutes > 0)
        const consultSecondsToSend = tempData.addConsultMinutes > 0
          ? tempData.addConsultMinutes * 60
          : 0

        const { data } = await adminApi.userUpdateUserFromAdminPost({
          user: {
            userId: tempData.userId,
            firstName: tempData.firstName,
            lastName: tempData.lastName,
            phoneNumber: tempData.phoneNumber,
            countryCode: tempData.countryCode,
            country: tempData.country,
            city: tempData.city,
            userType: tempData.userType,
            caneId: tempData.caneId,
            email: tempData.email,
            isAssistant: tempData.isAssistant,
            isPremium: tempData.isPremium,
            consultSeconds: consultSecondsToSend,
            consultExpiresDate: tempData.addConsultMinutes > 0 ? tempData.consultExpiresDate : undefined,
            assistantRegion: tempData.assistantRegion
          }
        })
        const index = this.list.findIndex(
          (v: any) => v.userId === data.data.userId
        )
        data.data.consultSeconds = data.data.consultSeconds / 60
        this.list.splice(index, 1, data.data)
        this.dialogFormVisible = false
        this.$notify({
          title: 'Success',
          message: tempData.addConsultMinutes > 0
            ? `Successfully added ${tempData.addConsultMinutes} minutes to user`
            : 'Successfully updated the user',
          type: 'success',
          duration: 2000
        })
      }
    })
  }

  // Dialog open handler - load data when opening
  private async onDialogOpen() {
    this.activeTab = 'basic'
    this.userLogs = []
    // Reset Devices/OTA tab so a different user's data can't leak through
    // when the dialog reopens for someone else.
    this.devicesTabUserDevices = []
    this.devicesTabAllPins = []
    this.devicesTabAvailableOtaFiles = []
    this.devicesTabLoadedFor = null
    if (this.tempUserData.userId) {
      this.loadUserEntitlements()
      this.loadFeatures()
      this.loadUserSubscriptions()
    }
  }

  @Watch('activeTab')
  private onActiveTabChange(tab: string) {
    if (tab === 'logs' && this.userLogs.length === 0 && this.tempUserData.userId) {
      this.fetchUserLogs()
    } else if (tab === 'devices' && this.tempUserData.userId &&
        this.devicesTabLoadedFor !== this.tempUserData.userId) {
      // Lazy-load on first tab visit, then idempotent — re-clicking the
      // same tab for the same user doesn't refetch unless we explicitly
      // reset `devicesTabLoadedFor` (happens on dialog reopen + after
      // pin mutations).
      this.loadDevicesTab()
    }
  }

  private async loadDevicesTab() {
    const userId = this.tempUserData.userId
    if (!userId) return
    this.devicesTabLoading = true
    try {
      const { data } = await getUserOtaInfo(userId)
      const payload = data.data || {}
      this.devicesTabUserDevices = payload.devices || []
      this.devicesTabAllPins = (payload.overrides || []).map((o: any) => ({
        key: o.deviceSerialNumber || 'user-scope',
        deviceSerial: o.deviceSerialNumber,
        otaFileId: o.otaFileId,
        appVersion: o.otaFile?.applicationVersionName || '',
        wirelessVersion: o.otaFile?.wirelessVersionName || null,
        reason: o.reason,
        setBy: o.setBy,
        createdAt: o.createdAt
      }))
      this.devicesTabAvailableOtaFiles = (payload.activeOtaFiles || []).map((f: any) => ({
        id: f.id,
        application_version_name: f.applicationVersionName,
        wireless_version_name: f.wirelessVersionName,
        is_mandatory: f.isMandatory
      }))
      this.devicesTabLoadedFor = userId
    } catch (err: any) {
      this.$message.error(this.$t('userDialog.loadDevicePinFailed', { msg: err.message || '' }) as string)
    } finally {
      this.devicesTabLoading = false
    }
  }

  // Pins applying to a specific cane = device-scope rows for that serial
  // plus any user-scope (NULL) row that hits whichever cane the user opens.
  private pinsForDevice(serial: string): PinListItem[] {
    return this.devicesTabAllPins.filter(
      p => p.deviceSerial === serial || p.deviceSerial === null
    )
  }

  private async onDevicesTabPinSaved() {
    // Force a refetch even though devicesTabLoadedFor matches — the pin
    // mutation invalidated the cached state.
    this.devicesTabLoadedFor = null
    await this.loadDevicesTab()
  }

  private async removePinFromDevicesTab(p: PinListItem) {
    const userId = this.tempUserData.userId
    if (!userId) return
    const isUserScope = p.deviceSerial === null
    const scopeLabel = isUserScope ? this.$t('otaPin.allCanesScope') as string : p.deviceSerial
    try {
      await this.$confirm(
        this.$t('otaPin.removeConfirm', { version: p.appVersion, scope: scopeLabel }) as string,
        this.$t('otaPin.removeTitle') as string,
        {
          confirmButtonText: this.$t('otaPin.removeConfirmButton') as string,
          cancelButtonText: this.$t('table.cancel') as string
        }
      )
    } catch {
      return
    }
    this.devicesTabPinRemovingKey = p.key
    try {
      await clearUserOtaOverride(
        userId,
        isUserScope ? undefined : (p.deviceSerial || undefined),
        isUserScope
      )
      this.$message.success(this.$t('otaPin.removed') as string)
      this.devicesTabLoadedFor = null
      await this.loadDevicesTab()
    } catch (err: any) {
      this.$message.error(this.$t('otaPin.removeFailed', { msg: err.message || '' }) as string)
    } finally {
      this.devicesTabPinRemovingKey = null
    }
  }

  // Permanent account erasure (KVKK/GDPR request). Runs the backend purge as a
  // dry run first so the operator sees what will go, then asks for the e-mail
  // to be typed back before the real delete.
  private async handlePurgeUser() {
    const { userId, email } = this.tempUserData
    if (!userId) return
    this.purgeLoading = true
    let preview: any
    try {
      const { data } = await request({ url: '/admin/users/purge', method: 'post', data: { userId, dryRun: true } })
      preview = data?.data
    } catch (err: any) {
      this.$message.error(this.$t('userDialog.deletionPreviewFailed', { msg: err.message || '' }) as string)
      return
    } finally {
      this.purgeLoading = false
    }
    const rows = Object.values(preview?.deleted || {}).reduce((sum: number, n: any) => sum + Number(n || 0), 0)
    const devices = preview?.devicesUnbound || 0
    const target = String(email || userId)
    try {
      await this.$prompt(
        this.$t('userDialog.purgeConfirm', {
          target,
          rows,
          devices,
          field: email ? this.$t('userDialog.purgeFieldEmail') : this.$t('userDialog.purgeFieldUserId')
        }) as string,
        this.$t('userDialog.deleteAccount') as string,
        {
          confirmButtonText: this.$t('userDialog.deletePermanently') as string,
          cancelButtonText: this.$t('table.cancel') as string,
          confirmButtonClass: 'el-button--danger',
          inputValidator: (v: string) => (v || '').trim().toLowerCase() === target.toLowerCase() || (this.$t('userDialog.valueMismatch') as string)
        }
      )
    } catch {
      return
    }
    this.purgeLoading = true
    try {
      const { data } = await request({
        url: '/admin/users/purge',
        method: 'post',
        data: { userId, reason: 'admin_panel_delete' }
      })
      if (!data?.data?.userRowDeleted) throw new Error(data?.status?.message || 'purge_failed')
      this.$message.success(this.$t('userDialog.accountDeleted') as string)
      this.dialogFormVisible = false
      this.fetchData()
    } catch (err: any) {
      this.$message.error(this.$t('userDialog.deleteAccountFailed', { msg: err.message || '' }) as string)
    } finally {
      this.purgeLoading = false
    }
  }

  private async fetchUserLogs() {
    if (!this.tempUserData.userId) return
    this.logsLoading = true
    try {
      const adminApi = new AdminApi()
      const { data } = await adminApi.getUserLogs(this.tempUserData.userId, {
        hours: this.logHours,
        limit: 200,
        severity: this.logSeverity || undefined
      })
      this.userLogs = data.data?.logs || []
    } catch (error: any) {
      console.error('Error fetching user logs:', error)
      this.$notify({
        title: 'Error',
        message: 'Failed to load user logs',
        type: 'error',
        duration: 3000
      })
      this.userLogs = []
    } finally {
      this.logsLoading = false
    }
  }

  private formatLogDate(ts: string): string {
    if (!ts) return '-'
    const d = new Date(ts)
    return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
  }

  // Load user entitlements
  private async loadUserEntitlements() {
    if (!this.tempUserData.userId) return
    try {
      this.entitlementsLoading = true
      const response = await getUserEntitlements(this.tempUserData.userId, true)
      // Backend returns { success: true, data: [...entitlements] }
      this.userEntitlements = response.data?.data || []
      console.log('Loaded entitlements:', this.userEntitlements)
    } catch (error: any) {
      console.error('Error loading entitlements:', error)
      this.$notify({
        title: 'Error',
        message: 'Failed to load user entitlements',
        type: 'error',
        duration: 3000
      })
    } finally {
      this.entitlementsLoading = false
    }
  }

  // Load available features
  private async loadFeatures() {
    try {
      const response = await listFeatures()
      // Backend returns { success: true, data: [...features] }
      this.availableFeatures = response.data?.data || []
      console.log('Loaded features:', this.availableFeatures)
    } catch (error: any) {
      console.error('Error loading features:', error)
      this.$notify({
        title: 'Warning',
        message: 'Failed to load features list',
        type: 'warning',
        duration: 3000
      })
    }
  }

  // Format date for display
  private formatDate(date: Date | string | null | undefined): string {
    if (!date) return 'N/A'
    const d = new Date(date)
    const day = d.getDate().toString().padStart(2, '0')
    const month = (d.getMonth() + 1).toString().padStart(2, '0')
    const year = d.getFullYear()
    return `${day}.${month}.${year}`
  }

  // Compact "x gün önce" style for download timestamps in the device cards.
  // Falls back to the full date when older than a month.
  private formatDateShort(date: Date | string | null | undefined): string {
    if (!date) return ''
    const d = new Date(date)
    const ms = Date.now() - d.getTime()
    const mins = Math.floor(ms / 60000)
    if (mins < 1) return this.$t('userDialog.justNow') as string
    if (mins < 60) return this.$t('userDialog.minutesAgo', { n: mins }) as string
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return this.$t('userDialog.hoursAgo', { n: hrs }) as string
    const days = Math.floor(hrs / 24)
    if (days < 30) return this.$tc('userDialog.daysAgo', days, { n: days }) as string
    return this.formatDate(date)
  }

  // Get status type for el-tag
  private getStatusType(row: Entitlement): string {
    if (!row.isActive) return 'danger'
    if (row.endDate) {
      const endDate = new Date(row.endDate)
      const now = new Date()
      const daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      if (daysRemaining <= 0) return 'danger'
      if (daysRemaining <= 30) return 'warning'
    }
    return 'success'
  }

  // Get status text
  private getStatusText(row: Entitlement): string {
    if (!row.isActive) return 'Revoked'
    if (row.endDate) {
      const endDate = new Date(row.endDate)
      const now = new Date()
      if (endDate <= now) return 'Expired'
    }
    return 'Active'
  }

  // Handle grant feature button
  private handleGrantFeature() {
    this.grantFormData = {
      featureKey: '',
      durationType: 'days',
      durationValue: 365,
      sourceType: 'admin_grant'
    }
    this.grantDialogVisible = true
  }

  // Calculate end date based on duration
  private calculateEndDate(durationType: string, durationValue: number): Date | null {
    if (durationType === 'unlimited') return null
    const endDate = new Date()
    switch (durationType) {
      case 'days':
        endDate.setDate(endDate.getDate() + durationValue)
        break
      case 'months':
        endDate.setMonth(endDate.getMonth() + durationValue)
        break
      case 'years':
        endDate.setFullYear(endDate.getFullYear() + durationValue)
        break
    }
    return endDate
  }

  // Submit grant feature
  private async submitGrantFeature() {
    if (!this.grantFormData.featureKey) {
      this.$notify({
        title: 'Error',
        message: 'Please select a feature',
        type: 'error',
        duration: 2000
      })
      return
    }

    try {
      const endDate = this.calculateEndDate(
        this.grantFormData.durationType,
        this.grantFormData.durationValue
      )

      const grantRequest: GrantEntitlementRequest = {
        userId: this.tempUserData.userId,
        featureKey: this.grantFormData.featureKey,
        sourceType: 'admin_grant',
        startDate: new Date(),
        endDate: endDate,
        metadata: {
          grantedBy: 'admin',
          grantedAt: new Date().toISOString()
        }
      }

      await grantEntitlement(grantRequest)

      this.$notify({
        title: 'Success',
        message: 'Feature granted successfully',
        type: 'success',
        duration: 2000
      })

      this.grantDialogVisible = false
      await this.loadUserEntitlements()
    } catch (error: any) {
      this.$notify({
        title: 'Error',
        message: error.message || 'Failed to grant feature',
        type: 'error',
        duration: 3000
      })
    }
  }

  // Handle revoke feature
  private handleRevokeFeature(row: Entitlement) {
    this.$prompt('Please enter the reason for revoking this feature:', 'Revoke Feature', {
      confirmButtonText: 'Revoke',
      cancelButtonText: 'Cancel',
      inputPlaceholder: 'Reason for revocation',
      type: 'warning'
    }).then(async({ value }: any) => {
      try {
        if (!row.id) {
          this.$message.error('Entitlement ID not found')
          return
        }
        await revokeEntitlementById(
          this.tempUserData.userId,
          row.id,
          value || 'Revoked by admin'
        )

        this.$notify({
          title: 'Success',
          message: 'Feature revoked successfully',
          type: 'success',
          duration: 2000
        })

        await this.loadUserEntitlements()
      } catch (error: any) {
        this.$notify({
          title: 'Error',
          message: error.message || 'Failed to revoke feature',
          type: 'error',
          duration: 3000
        })
      }
    }).catch(() => {
      // User cancelled
    })
  }

  // Handle extend feature
  private handleExtendFeature(row: Entitlement) {
    this.selectedEntitlement = row
    this.extendFormData = {
      durationValue: 30,
      durationType: 'days'
    }
    this.extendDialogVisible = true
  }

  // Load user subscription data
  private async loadUserSubscriptions() {
    if (!this.tempUserData.userId) return
    try {
      this.subscriptionsLoading = true
      this.userTransactions = []
      this.userSubscriptionEvents = []
      this.subscriptionSummary = null

      const [txResponse, evResponse] = await Promise.all([
        request({
          url: '/admin/subscription/transactions',
          method: 'get',
          params: { userId: this.tempUserData.userId, limit: 50 }
        }),
        request({
          url: '/admin/subscription/events',
          method: 'get',
          params: { userId: this.tempUserData.userId, limit: 50 }
        })
      ])

      this.userTransactions = txResponse.data?.data || []
      this.userSubscriptionEvents = evResponse.data?.data || []

      // Build summary from latest event
      this.buildSubscriptionSummary()
    } catch (error: any) {
      console.error('Error loading subscriptions:', error)
    } finally {
      this.subscriptionsLoading = false
    }
  }

  // Build subscription summary from events and transactions
  private buildSubscriptionSummary() {
    const events = this.userSubscriptionEvents
    const transactions = this.userTransactions

    if (events.length === 0 && transactions.length === 0) {
      this.subscriptionSummary = null
      return
    }

    // Get latest event for current status
    const latestEvent = events.length > 0 ? events[0] : null
    // Get first purchase
    const purchases = transactions.filter((t: any) =>
      (t.purchase_type || t.purchaseType) === 'new_purchase'
    )
    const firstPurchase = purchases.length > 0 ? purchases[purchases.length - 1] : (transactions.length > 0 ? transactions[transactions.length - 1] : null)

    this.subscriptionSummary = {
      platform: latestEvent?.platform || firstPurchase?.platform || null,
      status: latestEvent?.status || firstPurchase?.status || 'unknown',
      autoRenew: latestEvent?.auto_renew_status ?? latestEvent?.autoRenewStatus ?? null,
      productId: latestEvent?.product_id || latestEvent?.productId || firstPurchase?.product_id || firstPurchase?.productId || null,
      productName: latestEvent?.product_name || latestEvent?.productName || firstPurchase?.product_name || firstPurchase?.productName || null,
      firstPurchaseDate: firstPurchase?.purchase_date || firstPurchase?.purchaseDate || null,
      expiresDate: latestEvent?.expires_date || latestEvent?.expiresDate || null
    }
  }

  // Helper: display product name + code together for subscription rows.
  // Some packages.name values are translation keys (e.g. "vcs_packages_header",
  // "vcs_additional_30_header") rather than real labels — those are useless to
  // operations. Always show the product_id/code first since that uniquely
  // identifies the SKU; if there's a real human-readable name, append it.
  private displayProduct(row: any): string {
    const code = row?.product_id || row?.productId || row?.code || ''
    const name = row?.product_name || row?.productName || ''
    // Translation keys end with _header / _description / _title / _label
    const isKey = name && /_(header|description|title|label)$/.test(name)
    const realName = (!isKey && name && name !== code) ? name : ''
    if (code && realName) return `${code} (${realName})`
    if (code) return code
    if (realName) return realName
    return 'N/A'
  }

  // Helper: subscription status tag type
  private getSubscriptionStatusType(status: string): string {
    const map: Record<string, string> = {
      active: 'success',
      cancelled: 'warning',
      expired: 'danger',
      in_grace_period: 'warning',
      on_hold: 'warning',
      paused: 'info',
      refunded: 'danger',
      success: 'success',
      pending: 'info',
      failed: 'danger'
    }
    return map[status] || 'info'
  }

  // Helper: purchase type tag
  private getPurchaseTypeTag(type: string): string {
    const map: Record<string, string> = {
      new_purchase: 'success',
      renewal: '',
      restore: 'info',
      refund: 'danger'
    }
    return map[type] || 'info'
  }

  // Helper: transaction status tag
  private getTransactionStatusTag(status: string): string {
    return this.getSubscriptionStatusType(status)
  }

  // Helper: event type tag
  private getEventTypeTag(eventType: string): string {
    const map: Record<string, string> = {
      purchase: 'success',
      renewal: '',
      cancel: 'warning',
      expire: 'danger',
      refund: 'danger',
      grace_period: 'warning',
      hold: 'warning',
      pause: 'info',
      reactivate: 'success',
      renewal_failed: 'danger'
    }
    return map[eventType] || 'info'
  }

  // Helper: check if date is expiring soon (within 30 days)
  private isExpiringSoon(dateStr: string | null): boolean {
    if (!dateStr) return false
    const date = new Date(dateStr)
    const now = new Date()
    const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    return diffDays > 0 && diffDays <= 30
  }

  // Submit extend feature
  private async submitExtendFeature() {
    if (!this.selectedEntitlement) return

    try {
      // Calculate new end date
      const currentEndDate = this.selectedEntitlement.endDate
        ? new Date(this.selectedEntitlement.endDate)
        : new Date()

      let newEndDate: Date
      switch (this.extendFormData.durationType) {
        case 'days':
          newEndDate = new Date(currentEndDate)
          newEndDate.setDate(newEndDate.getDate() + this.extendFormData.durationValue)
          break
        case 'months':
          newEndDate = new Date(currentEndDate)
          newEndDate.setMonth(newEndDate.getMonth() + this.extendFormData.durationValue)
          break
        case 'years':
          newEndDate = new Date(currentEndDate)
          newEndDate.setFullYear(newEndDate.getFullYear() + this.extendFormData.durationValue)
          break
        default:
          newEndDate = currentEndDate
      }

      // Grant a new entitlement with the extended date
      const extendRequest: GrantEntitlementRequest = {
        userId: this.tempUserData.userId,
        featureKey: this.selectedEntitlement.feature?.featureKey || this.selectedEntitlement.featureKey || '',
        sourceType: 'admin_grant',
        startDate: new Date(),
        endDate: newEndDate,
        metadata: {
          extendedBy: 'admin',
          extendedAt: new Date().toISOString(),
          previousEndDate: currentEndDate.toISOString()
        }
      }

      await grantEntitlement(extendRequest)

      this.$notify({
        title: 'Success',
        message: `Feature extended until ${this.formatDate(newEndDate)}`,
        type: 'success',
        duration: 2000
      })

      this.extendDialogVisible = false
      await this.loadUserEntitlements()
    } catch (error: any) {
      this.$notify({
        title: 'Error',
        message: error.message || 'Failed to extend feature',
        type: 'error',
        duration: 3000
      })
    }
  }
}
